import { CONFIG } from "@/config-global";
import * as signalR from "@microsoft/signalr";
import { useRef, useState, useEffect, useCallback } from "react";

interface NotificationData {
  id: string;
  type: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  relatedId?: string;
  metadata?: any;
}

interface UseNotificationSignalRProps {
  userId: string;
  workspaceId?: string;
  onNotificationReceived?: (notification: NotificationData) => void;
  onNotificationCountUpdated?: (data: { unreadCount: number }) => void;
}

export function useNotificationSignalR({
  userId,
  workspaceId,
  onNotificationReceived,
  onNotificationCountUpdated,
}: UseNotificationSignalRProps) {
  const [connection, setConnection] = useState<signalR.HubConnection | null>(
    null
  );
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const connectionRef = useRef<signalR.HubConnection | null>(null);

  // Use refs to store callback functions to prevent dependency changes
  const callbacksRef = useRef({
    onNotificationReceived,
    onNotificationCountUpdated,
  });

  // Update refs when callbacks change
  useEffect(() => {
    callbacksRef.current = {
      onNotificationReceived,
      onNotificationCountUpdated,
    };
  });

  const startConnection = useCallback(async () => {
    if (!userId) return;

    // Clean up existing connection
    if (connectionRef.current) {
      try {
        await connectionRef.current.stop();
      } catch (error) {
        console.error(
          "Error stopping existing notification connection:",
          error
        );
      }
      connectionRef.current = null;
      setConnection(null);
      setIsConnected(false);
    }

    try {
      setConnectionError(null);

      // Get connection info from our backend API
      const response = await fetch(
        `${CONFIG.serverUrl}/api/signalr/notification-connect`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-user-id": userId,
            "x-workspace-id": workspaceId || "",
          },
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get SignalR notification connection info");
      }

      const { connectionInfo } = await response.json();

      // Create SignalR connection for notifications
      const newConnection = new signalR.HubConnectionBuilder()
        .withUrl(connectionInfo.url, {
          accessTokenFactory: () => connectionInfo.accessToken,
        })
        .withAutomaticReconnect([0, 2000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Information)
        .build();

      // Set up event handlers using refs
      newConnection.on(
        "notificationReceived",
        (notification: NotificationData) => {
          callbacksRef.current.onNotificationReceived?.(notification);
        }
      );

      newConnection.on(
        "notificationCountUpdated",
        (data: { unreadCount: number }) => {
          callbacksRef.current.onNotificationCountUpdated?.(data);
        }
      );

      // Connection state handlers
      newConnection.onreconnecting(() => {
        setIsConnected(false);
      });

      newConnection.onreconnected(async () => {
        setIsConnected(true);
        reconnectAttempts.current = 0;

        // Rejoin notification groups after reconnection
        if (newConnection.connectionId) {
          await joinNotificationGroups(newConnection.connectionId);
        }
      });

      newConnection.onclose((error) => {
        setIsConnected(false);

        if (error && reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          setTimeout(startConnection, 5000 * reconnectAttempts.current);
        }
      });

      // Start the connection
      await newConnection.start();
      connectionRef.current = newConnection;
      setConnection(newConnection);
      setIsConnected(true);

      // Join notification groups after connection is established
      if (newConnection.connectionId) {
        await joinNotificationGroups(newConnection.connectionId);
      }
    } catch (error) {
      console.error(
        "❌ Error starting notification SignalR connection:",
        error
      );
      setConnectionError(
        error instanceof Error ? error.message : "Connection failed"
      );

      if (reconnectAttempts.current < maxReconnectAttempts) {
        reconnectAttempts.current++;
        setTimeout(startConnection, 5000 * reconnectAttempts.current);
      }
    }
  }, [userId, workspaceId]);

  const joinNotificationGroups = useCallback(
    async (connectionId: string) => {
      if (!userId) return;

      try {
        // Join user-specific group
        await fetch(`${CONFIG.serverUrl}/api/signalr/join-notification-group`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userId,
            workspaceId,
            connectionId,
            groupType: "user",
          }),
          credentials: "include",
        });

        // Join workspace group if workspaceId is provided
        if (workspaceId) {
          await fetch(
            `${CONFIG.serverUrl}/api/signalr/join-notification-group`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                userId,
                workspaceId,
                connectionId,
                groupType: "workspace",
              }),
              credentials: "include",
            }
          );
        }
      } catch (error) {
        console.error("❌ Error joining notification groups:", error);
        // Don't fail the connection if group joining fails
      }
    },
    [userId, workspaceId]
  );

  const disconnect = useCallback(async () => {
    if (connection) {
      try {
        await connection.stop();
      } catch (error) {
        console.error(
          "❌ Error stopping notification SignalR connection:",
          error
        );
      }
      setConnection(null);
      setIsConnected(false);
    }
  }, [connection]);

  useEffect(() => {
    if (!userId) return;

    startConnection();

    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop().catch(console.error);
        connectionRef.current = null;
      }
    };
  }, [userId, workspaceId, startConnection]);

  return {
    connection,
    isConnected,
    connectionError,
    disconnect,
    reconnect: startConnection,
  };
}
